import { apiUrls } from '../config/api-config';

export interface SavedConfig {
  id: string;
  name: string;
  triggers: {
    en: string[];
    fr: string[];
    ar: string[];
  };
  categories: {
    en: string[];
    fr: string[];
    ar: string[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface SavedConfigSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  sessionId?: string;
}

export class ConfigStorageService {
  private static readonly LOCAL_STORAGE_KEY = 'storm_saved_configs';

  // Save configuration to server
  static async saveConfig(name: string, config: any, sessionId: string): Promise<{ success: boolean; message: string; configId?: string }> {
    try {
      const response = await fetch(apiUrls.saveConfig(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, config, sessionId })
      });

      const result = await response.json();
      
      if (result.success) {
        // Also save to localStorage as backup
        this.saveToLocalStorage(result.configId, name, config);
      }

      return result;
    } catch (error) {
      // Fallback to localStorage if server fails
      const configId = `local_${Date.now()}`;
      this.saveToLocalStorage(configId, name, config);
      
      return {
        success: true,
        message: 'Configuration saved locally (server unavailable)',
        configId
      };
    }
  }

  // Load all saved configurations
  static async loadSavedConfigs(): Promise<SavedConfigSummary[]> {
    try {
      const response = await fetch(apiUrls.loadConfigs());
      const result = await response.json();
      
      if (result.success) {
        // Merge with localStorage configs
        const localConfigs = this.loadFromLocalStorage();
        const serverConfigs = result.configs || [];
        
        // Combine and deduplicate
        const allConfigs = [...serverConfigs];
        localConfigs.forEach(local => {
          if (!serverConfigs.find(s => s.id === local.id)) {
            allConfigs.push(local);
          }
        });
        
        return allConfigs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      }
      
      return this.loadFromLocalStorage();
    } catch (error) {
      // Fallback to localStorage
      return this.loadFromLocalStorage();
    }
  }

  // Load specific configuration
  static async loadConfig(configId: string): Promise<SavedConfig | null> {
    try {
      const response = await fetch(apiUrls.loadConfig(configId));
      const result = await response.json();
      
      if (result.success) {
        return result.config;
      }
      
      return null;
    } catch (error) {
      // Fallback to localStorage
      return this.loadFromLocalStorage(configId);
    }
  }

  // Delete configuration
  static async deleteConfig(configId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(apiUrls.deleteConfig(configId), {
        method: 'DELETE'
      });

      const result = await response.json();
      
      // Also remove from localStorage
      this.removeFromLocalStorage(configId);
      
      return result;
    } catch (error) {
      // Fallback to localStorage
      this.removeFromLocalStorage(configId);
      return {
        success: true,
        message: 'Configuration deleted locally'
      };
    }
  }

  // LocalStorage methods
  private static saveToLocalStorage(configId: string, name: string, config: any): void {
    try {
      const savedConfigs = this.loadFromLocalStorage();
      const newConfig: SavedConfigSummary = {
        id: configId,
        name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update or add config
      const existingIndex = savedConfigs.findIndex(c => c.id === configId);
      if (existingIndex >= 0) {
        savedConfigs[existingIndex] = { ...savedConfigs[existingIndex], ...newConfig };
      } else {
        savedConfigs.push(newConfig);
      }

      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(savedConfigs));
      
      // Save full config data separately
      const fullConfig: SavedConfig = {
        ...newConfig,
        triggers: config.triggers,
        categories: config.categories
      };
      localStorage.setItem(`${this.LOCAL_STORAGE_KEY}_${configId}`, JSON.stringify(fullConfig));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private static loadFromLocalStorage(configId?: string): SavedConfigSummary[] | SavedConfig | null {
    try {
      const data = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      if (!data) return configId ? null : [];

      const configs = JSON.parse(data);
      
      if (configId) {
        // Load specific config
        const fullConfigData = localStorage.getItem(`${this.LOCAL_STORAGE_KEY}_${configId}`);
        return fullConfigData ? JSON.parse(fullConfigData) : null;
      }
      
      return configs;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return configId ? null : [];
    }
  }

  private static removeFromLocalStorage(configId: string): void {
    try {
      const savedConfigs = this.loadFromLocalStorage() as SavedConfigSummary[];
      const filtered = savedConfigs.filter(c => c.id !== configId);
      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(filtered));
      localStorage.removeItem(`${this.LOCAL_STORAGE_KEY}_${configId}`);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }

  // Auto-save current configuration
  static async autoSaveCurrentConfig(config: any, sessionId: string): Promise<void> {
    try {
      const autoSaveName = `Auto-save ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
      await this.saveConfig(autoSaveName, config, sessionId);
    } catch (error) {
      console.error('Error auto-saving configuration:', error);
    }
  }
}
